import { useCallback, useState, useMemo } from 'react'

import styled from '@emotion/styled'
import AccountBoxIcon from '@mui/icons-material/AccountBox'
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined'
import EmailIcon from '@mui/icons-material/Email'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import LogoutIcon from '@mui/icons-material/Logout'
import MusicVideoIcon from '@mui/icons-material/MusicVideo'
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import MUILink from '@mui/material/Link'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import { useQueryClient } from 'react-query'

import { useAuth } from '@redwoodjs/auth'
import { Link } from '@redwoodjs/router'

import ProfileImageCell from 'src/components/ProfileImageCell'
import useProfile from 'src/hooks/useProfile'
import discordImage from 'src/images/discord.png'
import twitterImage from 'src/images/twitter.png'
import { fanAppURL } from 'src/lib/env'

const ImageIcon = styled.img`
  max-width: 24px;
  max-height: 24px;
`

interface DrawerContentProps {
  toggleDrawer?: (
    open: boolean
  ) => (event: React.KeyboardEvent | React.MouseEvent) => void
}

const DrawerContent = ({ toggleDrawer }: DrawerContentProps) => {
  const { logOut } = useAuth()
  const { data: profile } = useProfile()
  const queryClient = useQueryClient()

  const menuItems = useMemo(
    () => [
      {
        Icon: () => <MusicVideoIcon sx={{ fontSize: '24px' }} />,
        label: 'Song Stories',
        href: '/',
        onClick: null,
      },
      // {
      //   Icon: () => <GroupsOutlinedIcon sx={{ fontSize: '24px' }} />,
      //   label: 'Fans',
      //   href: '/fan-dashboard',
      //   onClick: null,
      // },
      // {
      //   Icon: () => <AnalyticsOutlinedIcon sx={{ fontSize: '24px' }} />,
      //   label: 'Analytics',
      //   href: `/artist/${profile?.artist?.slug}/analytics`,
      //   onClick: null,
      // },
      // {
      //   Icon: () => <AccountBoxIcon sx={{ fontSize: '24px' }} />,
      //   label: 'Account',
      //   href: '/settings/account',
      //   onClick: null,
      // },
      null,
      {
        Icon: () => <HelpOutlineIcon sx={{ fontSize: '24px' }} />,
        label: 'Help & Support',
        href: 'https://www.majr.tech/post/getting-started-on-majr',
        onClick: null,
      },
      {
        Icon: () => <PrivacyTipOutlinedIcon sx={{ fontSize: '24px' }} />,
        label: 'Data Principles',
        href: 'https://majr.tech/dataprinciples',
        onClick: null,
      },
    ],
    [profile]
  )

  const socials = [
    {
      Icon: () => (
        <ImageIcon sx={{ fontSize: '24px' }} src={discordImage} alt="discord" />
      ),
      label: 'majr on Discord',
      href: 'https://discord.gg/zra3rCAGKA',
    },
    {
      Icon: () => (
        <ImageIcon sx={{ fontSize: '24px' }} src={twitterImage} alt="discord" />
      ),
      label: 'majr on Twitter',
      href: 'https://twitter.com/majrapp',
    },
    {
      Icon: () => <EmailIcon sx={{ fontSize: '24px' }} />,
      label: 'Send Feedback',
      href: 'mailto:feedback@majr.tech?subject=Feedback',
    },
  ]

  const handleSignOut = useCallback(() => {
    queryClient.clear()
    localStorage.clear()
    logOut()
  }, [])

  return (
    <Box
      sx={{ width: '300px', position: 'relative' }}
      flexGrow={1}
      display="flex"
      flexDirection="column"
      role="presentation"
      justifyContent="space-between"
      onClick={toggleDrawer?.(false)}
      onKeyDown={toggleDrawer?.(false)}
      color="text.secondary"
    >
      {profile && (
        <Box sx={{ p: 2 }}>
          <ProfileImageCell size={128} />
          <Typography variant="h5" color="text.primary">
            {profile.user?.realName || ''}
          </Typography>
          <Typography variant="subtitle1">{profile.user.email}</Typography>
          {profile.artist?.name && (
            <Typography variant="subtitle1">
              Performing as{' '}
              <MUILink
                sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
                href={`${fanAppURL}/${profile.artist?.slug}`}
                target="_blank"
              >
                {profile.artist.name}
              </MUILink>
            </Typography>
          )}
        </Box>
      )}

      <Divider />

      <List
        sx={{
          marginBottom: 'auto',
        }}
      >
        {menuItems.map((item, i) => {
          let hrefUrl = item?.href
          if (hrefUrl && profile) {
            hrefUrl = hrefUrl.replace('slug', profile.artist?.slug)
          }
          return item ? (
            <ListItem
              key={item.label}
              button
              component={item?.href?.startsWith('/') ? Link : 'a'}
              to={hrefUrl}
              href={hrefUrl}
              target="_blank"
            >
              <ListItemIcon sx={{ minWidth: 0, pr: 2 }}>
                <item.Icon />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ fontSize: '14px' }}
                primary={item.label}
              />
            </ListItem>
          ) : (
            <Box key={i} sx={{ minHeight: 30 }} />
          )
        })}
      </List>
      <List
        sx={{
          marginBottom: 'auto',
        }}
        subheader={
          <>
            <Typography px={2} variant="h6" color="text.disabled">
              Community
            </Typography>
            <Divider />
          </>
        }
      >
        {socials.map((item) => (
          <ListItem
            key={item.label}
            button
            component="a"
            href={item.href}
            target="_blank"
          >
            <ListItemIcon sx={{ minWidth: 0, pr: 2 }}>
              <item.Icon />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ fontSize: '14px' }}
              primary={item.label}
            />
          </ListItem>
        ))}
      </List>
      {/* <List
        sx={{
          justifySelf: 'flex-end',
        }}
      >
        <ListItem button onClick={handleSignOut}>
          <ListItemIcon>
            <LogoutIcon sx={{ fontSize: '24px' }} />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{ fontSize: '14px' }}
            primary="Sign Out"
          />
        </ListItem>
      </List> */}
    </Box>
  )
}

export default DrawerContent
